import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Search } from 'lucide-react'
import notify from 'components/ui/notify'
import { initializeMyPartners } from 'slices/myPartnersSlice'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Button } from 'components/ui/button'
import { Spinner } from 'components/ui/spinner'
import { addPartnerToProject } from 'slices/projectSlice'

const LocationAddPartnerPopup = ({ project }) => {
	const dispatch = useDispatch()

	const projectId = project._id

	const [searchValue, setSearchValue] = useState('')

	const userLogin = useSelector(state => state.userLogin)
	const { userInfo: user } = userLogin

	const { myPartners, isLoading } = useSelector(state => {
		const myPartnerIds = state.myPartners.ids
		const myPartners = myPartnerIds.map(id => state.myPartners.entities[id])
		return { myPartners, isLoading: state.myPartners.isLoading }
	})

	useEffect(() => {
		dispatch(initializeMyPartners())
	}, [dispatch, user])

	function isInProject(myPartner) {
		return !!project?.partners?.find(
			projectPartner => projectPartner.partnerUserId._id === myPartner.partner._id,
		)
	}

	function handleAddPartner(partner) {
		dispatch(
			addPartnerToProject({
				projectId: projectId,
				partner,
			}),
		)

		notify({
			title: `Partner Added!`,
			text: `Good job. ${partner.name} will be added as a partner to this project, pending ${partner.firstName}'s acceptance. An acknowledgement of this request has been sent to ${partner.firstName} as well as a cc to you.`,
			icon: 'success',
			button: 'Ok',
		})
	}

	const filtered = myPartners
		.filter(p => !isInProject(p))
		.filter(p => {
			if (!searchValue) return true
			const lower = searchValue.toLowerCase()
			return (
				(p.partner?.name || '').toLowerCase().includes(lower) ||
				(p.partner?.company || '').toLowerCase().includes(lower) ||
				(p.partner?.email || '').toLowerCase().includes(lower)
			)
		})

	return (
		<div className="w-full">
			{/* Search */}
			<div className="relative mb-4">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
				<input
					type="text"
					placeholder="Search partners..."
					value={searchValue}
					onChange={e => setSearchValue(e.target.value)}
					className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#0080fe] focus:outline-none focus:ring-1 focus:ring-[#0080fe]"
				/>
			</div>

			{/* Partners list */}
			{isLoading ? (
				<div className="py-8">
					<Spinner />
				</div>
			) : filtered.length === 0 ? (
				<div className="py-12 text-center text-sm text-gray-400">
					{searchValue ? 'No partners match your search.' : 'No available partners to add.'}
				</div>
			) : (
				<div className="divide-y divide-gray-100">
					{filtered.map(partner => (
						<div key={partner.partner._id} className="flex items-center gap-3 py-3">
							<Avatar className="h-9 w-9 flex-shrink-0">
								<AvatarImage src={partner.partner.image} alt={partner.partner.name || ''} />
								<AvatarFallback className="text-sm bg-gray-100 text-gray-600">
									{(partner.partner.name || '?').charAt(0).toUpperCase()}
								</AvatarFallback>
							</Avatar>
							<span className="text-sm font-medium text-gray-900 min-w-0 truncate">
								{partner.partner.name}
							</span>
							<span className="text-sm text-gray-500 min-w-0 truncate">
								{partner.partner.title}
							</span>
							<span className="text-sm text-gray-400 min-w-0 truncate">
								{partner.partner.company}
							</span>
							<div className="ml-auto flex-shrink-0">
								<Button variant="primary" isOutline className="text-xs" onClick={() => handleAddPartner(partner)}>
									Add Partner
								</Button>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	)
}

export default LocationAddPartnerPopup
